import { useId } from 'react';
import { T, useGT } from 'gt-react';
import type { ReviewLocationOption } from './reviewLocationFilter';
import { segmentedButtonClass } from './segmentedButtonClass';

/**
 * The Location filter of the review page and the item catalog, laid out like
 * the Division row: "All"
 * (no narrowing), then one button per location of RD, several at a time.
 * Disabled — with a tooltip saying why — while RD isn't one of the selected
 * divisions, since its locations are the only ones offered. A double-click
 * picks one location alone, as on the Division row.
 */
export function LocationSelector({
  divisionTitle,
  options,
  value,
  enabled,
  onToggle,
  onClear,
  onIsolate,
}: {
  divisionTitle: string;
  options: readonly ReviewLocationOption[];
  /** The picked CDB keys; empty = every location. */
  value: readonly string[];
  enabled: boolean;
  onToggle: (key: string) => void;
  onClear: () => void;
  onIsolate: (key: string) => void;
}) {
  const gt = useGT();
  const labelId = useId();
  const disabledHint = enabled
    ? undefined
    : gt('Select {division} to filter by its locations', {
        context:
          'tooltip on the disabled Location filter: it only applies to a division split by location (RD)',
        division: divisionTitle,
      });
  const picked = new Set(value);
  return (
    <div className="flex items-center gap-2 flex-wrap" title={disabledHint}>
      <span
        id={labelId}
        className="text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap"
      >
        <T context="label for the review page's location filter (RD's locations)">
          Location
        </T>
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex rounded-md border overflow-hidden text-xs"
      >
        <button
          type="button"
          aria-pressed={enabled && picked.size === 0}
          disabled={!enabled}
          className={segmentedButtonClass(0, picked.size === 0, !enabled)}
          onClick={onClear}
        >
          <T context="location filter, every location (no narrowing)">All</T>
        </button>
        {options.map((option, index) => {
          const isActive = picked.has(option.key);
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={enabled && isActive}
              aria-label={option.label}
              disabled={!enabled}
              title={enabled ? option.label : undefined}
              className={segmentedButtonClass(index + 1, isActive, !enabled)}
              onClick={() => onToggle(option.key)}
              onDoubleClick={() => onIsolate(option.key)}
            >
              {option.code}
            </button>
          );
        })}
      </div>
    </div>
  );
}
